import React from 'react';
import { Alert, Box, Button, ButtonBase, Chip, Typography } from '@mui/material';
import { Instruction } from './lib/types';
import { INSTRUCTION_STATUS_LABEL, REPLY_STATUS_LABEL, threadState } from './lib/selectors';
import { fmtIct } from './lib/ict';
import { useNow } from './hooks/useNow';
import { useBotOpsActions } from './BotOpsActions';
import { UnreadBadge } from './chips';
import { Markdown } from './Markdown';
import { ACCENT, FONT, RADIUS, TONE } from './lib/brand';

const KIND_LABEL = { instruction: 'Instruction', question: 'Question', override: 'Instruct instead' } as const;
const REPLY_COLOR: Record<string, { fg: string; bg: string }> = {
  done: { fg: TONE.good.fg, bg: TONE.good.bg }, in_progress: { fg: TONE.info.fg, bg: TONE.info.bg }, queued: { fg: TONE.neutral.fg, bg: TONE.neutral.bg },
  blocked: { fg: TONE.warn.fg, bg: TONE.warn.bg }, failed: { fg: TONE.bad.fg, bg: TONE.bad.bg }, declined_scope: { fg: ACCENT.lifescribe.ink, bg: ACCENT.lifescribe.soft },
};

/**
 * One thread with a bot: what Grant sent, how far it got (⏳ Sent → ✓ Delivered
 * → ✓ <bot> acknowledged), and the bot's replies as sanitized Markdown. A thread
 * that Grok Bot has not acknowledged within a minute offers "Resend nudge"; one
 * with no bot ack after a day says so.
 */
export function InstructionThread({
  instruction, botName, expanded, onToggle,
}: {
  instruction: Instruction; botName: string; expanded: boolean; onToggle: () => void;
}) {
  const api = useBotOpsActions();
  const now = useNow(15000);
  const st = threadState(instruction, now);
  const [nudgeMsg, setNudgeMsg] = React.useState<{ ok: boolean; message: string } | null>(null);
  const [nudging, setNudging] = React.useState(false);
  const replies = instruction.bot_reply || [];

  // Opening an unread thread marks it seen.
  const { markSeen } = api;
  const unread = instruction.unread;
  React.useEffect(() => {
    if (expanded && unread && api.canWrite) markSeen(instruction.id);
  }, [expanded, unread, instruction.id, markSeen, api.canWrite]);

  const stage = (done: boolean, text: string) => (
    <Box component="span" sx={{ color: done ? TONE.good.fg : 'text.secondary', fontWeight: done ? 600 : 400 }}>{text}</Box>
  );

  return (
    <Box data-testid={`thread-${instruction.id}`} sx={{ border: '1px solid', borderColor: unread ? TONE.bad.border : 'divider', borderRadius: RADIUS.lg, mb: 1, overflow: 'hidden' }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${KIND_LABEL[instruction.kind]} to ${botName}, ${INSTRUCTION_STATUS_LABEL[instruction.status] || instruction.status}${unread ? ', unread reply' : ''}`}
        sx={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'flex-start', gap: 1, p: 1.1, bgcolor: expanded ? 'action.hover' : 'transparent' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', flexWrap: 'wrap', mb: 0.3 }}>
            <Chip size="small" label={KIND_LABEL[instruction.kind]} sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />
            {instruction.kind !== 'question' && <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 700, px: 0.7, borderRadius: RADIUS.sm, bgcolor: 'action.selected' }}>{instruction.priority}</Box>}
            <Chip size="small" variant="outlined" label={INSTRUCTION_STATUS_LABEL[instruction.status] || instruction.status} sx={{ height: 20, fontSize: 10.5 }} />
            {st.noAck24h && <Chip size="small" color="warning" label="No bot ack in 24 h" sx={{ height: 20, fontSize: 10.5 }} />}
            <UnreadBadge count={unread ? 1 : 0} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{fmtIct(instruction.created_at)}</Typography>
          </Box>
          <Typography sx={{ fontSize: 13, ...(expanded ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } : { overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }) }}>
            {instruction.grant_instruction}
          </Typography>
        </Box>
      </ButtonBase>

      {expanded && (
        <Box sx={{ px: 1.3, pb: 1.2 }}>
          <Typography variant="caption" component="div" sx={{ mt: 0.6 }} data-testid={`stage-${instruction.id}`} data-stage={st.stage}>
            {stage(true, '⏳ Sent')} {' → '}
            {stage(st.stage !== 'sent', st.stage === 'sent' ? 'Delivered to Grok Bot' : '✓ Delivered to Grok Bot')} {' → '}
            {stage(st.stage === 'acked', st.stage === 'acked' ? `✓ ${botName} acknowledged` : `${botName} acknowledged`)}
          </Typography>

          {st.notDelivered && (
            <Alert
              severity="warning"
              sx={{ mt: 1, py: 0 }}
              action={(
                <Button
                  size="small"
                  color="inherit"
                  disabled={nudging || !api.canWrite}
                  onClick={async () => { setNudging(true); setNudgeMsg(await api.nudge(instruction.id)); setNudging(false); }}
                >
                  Resend nudge
                </Button>
              )}
            >
              Not delivered yet (no ack in 1 min).
            </Alert>
          )}
          {st.webhookFailed && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>Grok Bot&apos;s webhook could not be reached after several tries. The message is safe in the queue.</Alert>}
          {nudgeMsg && <Alert severity={nudgeMsg.ok ? 'success' : 'info'} sx={{ mt: 1, py: 0 }} role="status">{nudgeMsg.message}</Alert>}

          {replies.length === 0 ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>No reply yet.</Typography>
          ) : replies.map((r, i) => {
            const c = REPLY_COLOR[r.status || ''] || { fg: TONE.neutral.fg, bg: TONE.neutral.bg };
            return (
              <Box key={`${r.at}-${i}`} sx={{ mt: 1, borderLeft: '3px solid', borderColor: c.fg, pl: 1.2 }} data-testid="thread-reply">
                <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', mb: 0.4, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{r.source ? r.source.replace(/^bot:/, '') : botName}</Typography>
                  {r.status && <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, px: 0.8, borderRadius: RADIUS.sm, color: c.fg, bgcolor: c.bg }}>{REPLY_STATUS_LABEL[r.status] || r.status}</Box>}
                  <Typography variant="caption" color="text.secondary">{fmtIct(r.at)}</Typography>
                </Box>
                {r.text ? <Markdown>{r.text}</Markdown> : <Typography variant="caption" color="text.secondary">No text.</Typography>}
                {r.result_path && <Typography variant="caption" sx={{ fontFamily: FONT.mono, color: 'text.secondary', wordBreak: 'break-all' }} component="div">{r.result_path}</Typography>}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
